import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Save, Printer, Eye, ArrowUp, ArrowDown, Wand2, Package, Calculator } from "lucide-react";
import { toast } from "sonner";
import PrescriptionPreview from "@/components/PrescriptionPreview";

// Parse frequency string → doses per day (best-effort)
// Returns null when not computable (e.g. PRN / As needed)
function parseFrequencyPerDay(freq) {
  if (!freq) return null;
  const t = String(freq).toLowerCase();
  if (/(prn|as needed)/.test(t)) return null;
  if (/(at bedtime|hs\b)/.test(t)) return 1;
  if (/(four|qid|4 times|every 6 hours|q6h)/.test(t)) return 4;
  if (/(three|tid|3 times|every 8 hours|q8h)/.test(t)) return 3;
  if (/(twice|bid|2 times|every 12 hours|q12h)/.test(t)) return 2;
  if (/(once|od\b|qd\b|daily|every 24 hours)/.test(t)) return 1;
  if (/every 4 hours|q4h/.test(t)) return 6;
  if (/every 2 hours|q2h/.test(t)) return 12;
  const m = t.match(/(\d+)\s*(times|x)\s*(?:a|per)?\s*day/);
  if (m) return parseInt(m[1], 10);
  const hr = t.match(/every\s+(\d+)\s*hour/);
  if (hr) return Math.round(24 / parseInt(hr[1], 10));
  return null;
}

// Parse duration string → days
function parseDurationDays(d) {
  if (!d) return null;
  const t = String(d).toLowerCase().trim();
  if (/(maintenance|as needed|prn|ongoing)/.test(t)) return null;
  const m = t.match(/(\d+)\s*(day|d|week|wk|month|mo|year|yr)/);
  if (m) {
    const n = parseInt(m[1], 10);
    const u = m[2];
    if (u.startsWith("week") || u === "wk") return n * 7;
    if (u.startsWith("month") || u === "mo") return n * 30;
    if (u.startsWith("year") || u === "yr") return n * 365;
    return n;
  }
  const num = t.match(/^(\d+)$/);
  return num ? parseInt(num[1], 10) : null;
}

// Default unit from preparation
function unitFromPreparation(prep) {
  if (!prep) return "";
  const p = prep.toLowerCase();
  if (p.includes("tablet")) return "tablet";
  if (p.includes("capsule")) return "capsule";
  if (p.includes("syrup") || p.includes("suspension") || p.includes("solution") || p.includes("drops") || p.includes("ml")) return "mL";
  if (p.includes("vial") || p.includes("ampule")) return "vial";
  if (p.includes("sachet")) return "sachet";
  if (p.includes("patch")) return "patch";
  if (p.includes("inhaler") || p.includes("spray")) return "puff";
  return prep.toLowerCase();
}

const FREQ_PRESETS = [
  "Once daily", "Twice daily (BID)", "Three times daily (TID)", "Four times daily (QID)",
  "Every 4 hours", "Every 6 hours", "Every 8 hours", "Every 12 hours",
  "As needed (PRN)", "Before meals", "After meals", "At bedtime (HS)",
];
const DURATION_PRESETS = ["3 days", "5 days", "7 days", "10 days", "14 days", "1 month", "3 months", "Maintenance", "As needed"];

function emptyRow() {
  return {
    medicine_id: "", medicine_name: "", dosage: "", frequency: "", duration: "", instruction: "",
    preparation: "", duration_days: null, total_quantity: null, unit: "", is_supply: false,
    _qty_override: false,
  };
}

function Autocomplete({ value, onChange, options, getLabel, getKey, onSelect, placeholder, testid }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const filtered = useMemo(() => {
    const v = (value || "").toLowerCase();
    return (options || []).filter((o) => getLabel(o).toLowerCase().includes(v)).slice(0, 8);
  }, [value, options, getLabel]);
  return (
    <div className="relative" ref={ref}>
      <Input
        value={value || ""}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        data-testid={testid}
      />
      {open && filtered.length > 0 ? (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-56 overflow-auto">
          {filtered.map((o) => (
            <button
              key={getKey(o)}
              type="button"
              onClick={() => { onSelect(o); setOpen(false); }}
              className="block w-full text-left px-3 py-2 text-sm hover:bg-blue-50"
              data-testid={`autocomplete-option-${getKey(o)}`}
            >
              {getLabel(o)}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Vocab-backed combobox: shows preset + custom values, supports typing free text,
 * and offers an "Add new" option that persists to /api/vocab/{kind}.
 */
function VocabCombo({ kind, vocab, onVocabAdded, value, onChange, placeholder, testid }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const v = (value || "").trim();
  const filtered = useMemo(() => {
    const q = v.toLowerCase();
    return (vocab || []).filter((o) => o.toLowerCase().includes(q)).slice(0, 12);
  }, [v, vocab]);
  const exact = (vocab || []).some((o) => o.toLowerCase() === v.toLowerCase());
  const showAdd = v && !exact;

  const saveCustom = async () => {
    try {
      await api.post(`/vocab/${kind}`, { value: v });
      onVocabAdded?.(v);
      toast.success(`"${v}" saved`);
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <Input
        value={value || ""}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        data-testid={testid}
      />
      {open && (filtered.length > 0 || showAdd) ? (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-56 overflow-auto">
          {filtered.map((o) => (
            <button
              key={o}
              type="button"
              onClick={() => { onChange(o); setOpen(false); }}
              className="block w-full text-left px-3 py-2 text-sm hover:bg-blue-50"
              data-testid={`${testid}-opt-${o}`}
            >
              {o}
            </button>
          ))}
          {showAdd ? (
            <button
              type="button"
              onClick={saveCustom}
              className="block w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 text-emerald-700 border-t border-slate-200"
              data-testid={`${testid}-add-custom`}
            >
              + Save "{v}" as custom {kind}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default function NewPrescription() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const dupFromId = searchParams.get("from");
  const prefillPatientId = searchParams.get("patient");
  const returnTo = searchParams.get("returnTo") || "";
  const nav = useNavigate();

  const [patients, setPatients] = useState([]);
  const [medicines, setMedicines] = useState([]);
  const [settings, setSettings] = useState(null);
  const [patient, setPatient] = useState(null);
  const [patientQuery, setPatientQuery] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [followUpDate, setFollowUpDate] = useState("");
  const [diagnosis, setDiagnosis] = useState("");
  const [requestText, setRequestText] = useState("");
  const [requestGroups, setRequestGroups] = useState([]);
  const [requestOpen, setRequestOpen] = useState(false);
  const [rows, setRows] = useState([emptyRow()]);
  const [generalInstruction, setGeneralInstruction] = useState("");
  const [savingId, setSavingId] = useState(id || null);
  const [busy, setBusy] = useState(false);
  // Vocab (preparation, frequency, duration, unit) loaded from /api/vocab/*
  const [vocab, setVocab] = useState({ preparation: [], frequency: FREQ_PRESETS, duration: DURATION_PRESETS, unit: [] });
  const loadVocab = async () => {
    try {
      const [p, f, d, u] = await Promise.all([
        api.get("/vocab/preparation"),
        api.get("/vocab/frequency"),
        api.get("/vocab/duration"),
        api.get("/vocab/unit"),
      ]);
      setVocab({
        preparation: p.data?.values || [],
        frequency: f.data?.values || FREQ_PRESETS,
        duration: d.data?.values || DURATION_PRESETS,
        unit: u.data?.values || [],
      });
    } catch { /* fall back to presets */ }
  };
  const pushVocab = (kind, v) => setVocab((prev) => prev[kind].includes(v) ? prev : { ...prev, [kind]: [...prev[kind], v] });

  useEffect(() => {
    Promise.all([
      api.get("/patients"),
      api.get("/medicines"),
      api.get("/print-settings"),
    ]).then(([p, m, s]) => {
      const pats = p.data || [];
      setPatients(pats);
      setMedicines(m.data || []);
      setSettings(s.data || {});
      if (prefillPatientId && !id && !dupFromId) {
        const found = pats.find((x) => x.id === prefillPatientId);
        if (found) { setPatient(found); setPatientQuery(found.full_name); }
      }
    }).catch(() => {});
    loadVocab();
  }, []);

  // Load existing for edit / duplicate
  useEffect(() => {
    if (!id && !dupFromId) return;
    const fetchId = id || dupFromId;
    const action = id ? "edit" : "duplicate";
    api.get(`/prescriptions/${fetchId}`).then((r) => {
      const p = r.data;
      const ps = p.patient_snapshot || {};
      setPatient({ id: p.patient_id, ...ps });
      setPatientQuery(ps.full_name || "");
      setDate((p.date || new Date().toISOString()).slice(0, 10));
      setFollowUpDate((p.follow_up_date || "").slice(0, 10));
      setDiagnosis(p.diagnosis || "");
      setRequestText(p.request_text || "");
      const loadedRequestGroups = normalizeRequestGroupsForEdit(p.request_groups || [], p.request_text || "");
      setRequestGroups(loadedRequestGroups);
      setRequestOpen(loadedRequestGroups.length > 0 || !!(p.request_text || "").trim());
      setRows((p.medicines || []).length ? p.medicines.map((m) => ({ ...emptyRow(), ...m })) : [emptyRow()]);
      setGeneralInstruction(p.general_instruction || "");
      if (action === "edit") setSavingId(p.id);
    }).catch(() => toast.error("Could not load prescription"));
  }, [id, dupFromId]);

  const useLastForPatient = async () => {
    if (!patient?.id) { toast.error("Select patient first"); return; }
    try {
      const { data: last } = await api.get(`/prescriptions/last/patient/${patient.id}`);
      setRows((last.medicines || []).length ? last.medicines.map((m) => ({ ...emptyRow(), ...m })) : [emptyRow()]);
      setGeneralInstruction(last.general_instruction || "");
      toast.success("Loaded last prescription for patient");
    } catch {
      toast.error("No previous prescription for this patient");
    }
  };

  const REQUEST_TYPE_OPTIONS = [
    { value: "laboratory", label: "Laboratory Request" },
    { value: "radiology", label: "Radiology / X-Ray Request" },
    { value: "ultrasound", label: "Ultrasound Request" },
    { value: "cardiology", label: "Cardiology / ECG Request" },
    { value: "procedure", label: "Procedure Request" },
    { value: "referral", label: "Referral Request" },
    { value: "other", label: "Other Request" },
  ];

  const requestTypeLabel = (type) => {
    return REQUEST_TYPE_OPTIONS.find((x) => x.value === type)?.label || "Request";
  };

  const emptyRequestGroup = (type = "laboratory") => ({
    type,
    title: requestTypeLabel(type),
    items: "",
    notes: "",
  });

  const normalizeRequestGroupsForEdit = (groups, legacyText = "") => {
    if (Array.isArray(groups) && groups.length) {
      return groups.map((g) => ({
        type: g.type || "other",
        title: g.title || requestTypeLabel(g.type || "other"),
        items: Array.isArray(g.items) ? g.items.join("\n") : String(g.items || ""),
        notes: g.notes || "",
      }));
    }
    if ((legacyText || "").trim()) {
      return [{
        type: "other",
        title: "General Request",
        items: legacyText,
        notes: "",
      }];
    }
    return [];
  };

  const serializeRequestGroups = () => {
    return requestGroups
      .map((g) => ({
        type: g.type || "other",
        title: g.title || requestTypeLabel(g.type || "other"),
        items: String(g.items || "")
          .split(/\r?\n/)
          .map((x) => x.trim())
          .filter(Boolean),
        notes: g.notes || "",
      }))
      .filter((g) => g.items.length || String(g.notes || "").trim());
  };

  const addRequestGroup = (type = "laboratory") => {
    setRequestOpen(true);
    setRequestGroups((prev) => [...prev, emptyRequestGroup(type)]);
  };

  const updateRequestGroup = (idx, patch) => {
    setRequestGroups((prev) => prev.map((g, i) => {
      if (i !== idx) return g;
      const next = { ...g, ...patch };
      if (patch.type && !patch.title) next.title = requestTypeLabel(patch.type);
      return next;
    }));
  };

  const removeRequestGroup = (idx) => {
    setRequestGroups((prev) => prev.filter((_, i) => i !== idx));
  };



  const parseDosageQuantity = (dosage) => {
    const text = String(dosage || "").toLowerCase().trim();
    if (!text) return 1;

    // Examples:
    // "1 tablet" => 1
    // "2 tablets" => 2
    // "1/2 tab" => 0.5
    // "1 1/2 tab" => 1.5
    // "0.5 tablet" => 0.5
    const mixed = text.match(/^(\d+)\s+(\d+)\s*\/\s*(\d+)/);
    if (mixed) {
      const whole = Number(mixed[1]);
      const num = Number(mixed[2]);
      const den = Number(mixed[3]);
      if (den > 0) return whole + (num / den);
    }

    const fraction = text.match(/^(\d+)\s*\/\s*(\d+)/);
    if (fraction) {
      const num = Number(fraction[1]);
      const den = Number(fraction[2]);
      if (den > 0) return num / den;
    }

    const decimalOrWhole = text.match(/^(\d+(?:\.\d+)?)/);
    if (decimalOrWhole) {
      const n = Number(decimalOrWhole[1]);
      if (!Number.isNaN(n) && n > 0) return n;
    }

    return 1;
  };

  const computeTotalQuantity = (row) => {
    const dosageQty = parseDosageQuantity(row.dosage);
    const perDay = parseFrequencyPerDay(row.frequency);
    const days = parseDurationDays(row.duration);

    if (perDay != null && days != null) {
      return {
        duration_days: days,
        total_quantity: Math.ceil(dosageQty * perDay * days),
      };
    }

    return {
      duration_days: days,
      total_quantity: null,
    };
  };

  const addRow = () => setRows((r) => [...r, emptyRow()]);
  const removeRow = (i) => setRows((r) => r.length === 1 ? [emptyRow()] : r.filter((_, idx) => idx !== i));
  const moveRow = (i, dir) => setRows((r) => {
    const j = i + dir;
    if (j < 0 || j >= r.length) return r;
    const cp = [...r];
    [cp[i], cp[j]] = [cp[j], cp[i]];
    return cp;
  });
  const updateRow = (i, patch) => setRows((r) => r.map((row, idx) => {
    if (idx !== i) return row;
    const next = { ...row, ...patch };
    // Track if user manually edited the total → mark override
    if (Object.prototype.hasOwnProperty.call(patch, "total_quantity") && patch._qty_override !== false) {
      next._qty_override = true;
    }
    if (Object.prototype.hasOwnProperty.call(patch, "is_supply") && patch.is_supply) {
      // Supplies skip auto-compute and keep whatever total user typed (or empty)
      return next;
    }
    // Recompute total_quantity if not overridden and not a supply.
    // Formula: dosage quantity × frequency per day × duration days.
    // Example: "2 tablets" + "5 x a day" + "10 days" = 100 tablets.
    if (!next.is_supply && !next._qty_override) {
      const computed = computeTotalQuantity(next);
      next.duration_days = computed.duration_days;
      next.total_quantity = computed.total_quantity;
    }
    // Sync unit default from preparation when user hasn't set one
    if (Object.prototype.hasOwnProperty.call(patch, "preparation") && !next.unit) {
      next.unit = unitFromPreparation(next.preparation);
    }
    return next;
  }));

  const resetQty = (i) => setRows((r) => r.map((row, idx) => {
    if (idx !== i) return row;
    const next = { ...row, _qty_override: false };
    const computed = computeTotalQuantity(next);
    next.duration_days = computed.duration_days;
    next.total_quantity = computed.total_quantity;
    return next;
  }));

  const pickMedicine = (i, med) => {
    const prep = med.dosage_form || "";
    updateRow(i, {
      medicine_id: med.id,
      medicine_name: med.medicine_name,
      dosage: med.default_dosage || "",
      frequency: med.default_frequency || "",
      duration: med.default_duration || "",
      instruction: med.default_instruction || "",
      preparation: prep,
      unit: med.unit || unitFromPreparation(prep),
      _qty_override: false,
    });
  };

  const buildPayload = (status) => ({
    patient_id: patient?.id || "",
    date: new Date(date).toISOString(),
    medicines: rows
      .filter((r) => (r.medicine_name || "").trim())
      // Strip UI-only flags before persisting
      .map(({ _qty_override, ...rest }) => ({
        ...rest,
        total_quantity: rest.total_quantity == null || rest.total_quantity === "" ? null : Number(rest.total_quantity),
        duration_days: rest.duration_days == null || rest.duration_days === "" ? null : Number(rest.duration_days),
      })),
    general_instruction: generalInstruction || "",
    diagnosis: diagnosis || "",
    request_text: requestOpen ? (requestText || "") : "",
    request_groups: requestOpen ? serializeRequestGroups() : [],
    status: status,
    source_prescription_id: dupFromId || "",
    follow_up_date: followUpDate || "",
  });

  const save = async (status = "draft") => {
    if (!patient?.id) { toast.error("Please select a patient"); return null; }
    const hasMeds = rows.some((r) => (r.medicine_name || "").trim());
    if (!hasMeds && !diagnosis.trim()) {
      toast.error("Add at least one medicine or a diagnosis");
      return null;
    }
    setBusy(true);
    try {
      const payload = buildPayload(status);
      let result;
      if (savingId) {
        const { data } = await api.put(`/prescriptions/${savingId}`, payload);
        result = data;
      } else {
        const { data } = await api.post("/prescriptions", payload);
        result = data;
        setSavingId(data.id);
      }
      // Sync the follow-up date to the patient's next visit so it appears in the
      // Follow-ups module and on future printouts (single source of truth).
      if (patient?.id) {
        try { await api.put(`/patients/${patient.id}/next-visit`, { next_visit_date: followUpDate || "" }); } catch {}
      }
      toast.success(status === "draft" ? "Draft saved" : "Prescription saved");
      return result;
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const returnParam = returnTo ? `&returnTo=${encodeURIComponent(returnTo)}` : "";
  const saveAndPrint = async () => {
    const r = await save("saved");
    if (r?.id) nav(`/prescriptions/${r.id}/preview?print=1${returnParam}`);
  };
  const previewOnly = async () => {
    const r = await save("draft");
    if (r?.id) nav(`/prescriptions/${r.id}/preview${returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : ""}`);
  };

  const previewObj = useMemo(() => ({
    prescription_number: savingId ? "" : "DRAFT",
    patient_snapshot: patient || {},
    doctor_snapshot: {
      name: settings?.doctor_name || "",
      specialty: settings?.specialty || "",
      license_number: settings?.license_number || "",
      ptr_number: settings?.ptr_number || "",
      signature_image_url: settings?.signature_image_url || "",
    },
    date: new Date(date).toISOString(),
    follow_up_date: followUpDate || "",
    diagnosis: diagnosis || "",
    request_text: requestOpen ? (requestText || "") : "",
    request_groups: requestOpen ? serializeRequestGroups() : [],
    medicines: rows,
    general_instruction: generalInstruction,
  }), [patient, settings, date, followUpDate, diagnosis, requestOpen, requestText, requestGroups, rows, generalInstruction, savingId]);

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 p-4 md:p-6" data-testid="new-prescription-page">
      <div className="mx-auto h-[92vh] w-[95vw] max-w-[1500px] overflow-hidden rounded-2xl bg-white shadow-2xl border border-slate-200 flex flex-col">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-6 py-4 bg-white">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "Work Sans" }}>
              {savingId ? "Edit Prescription" : "New Prescription"}
            </h1>
            <p className="text-slate-500 text-sm mt-1">Full-width prescription form · preview opens only when requested</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => nav(-1)} disabled={busy} data-testid="btn-cancel">
              Cancel
            </Button>
            <Button variant="outline" onClick={() => save("draft")} disabled={busy} data-testid="btn-save-draft">
              <Save size={14} className="mr-2" /> Save Draft
            </Button>
            <Button variant="outline" onClick={previewOnly} disabled={busy} data-testid="btn-preview">
              <Eye size={14} className="mr-2" /> Save & Preview
            </Button>
            <Button onClick={saveAndPrint} className="bg-blue-600 hover:bg-blue-700" disabled={busy} data-testid="btn-save-print">
              <Printer size={14} className="mr-2" /> Save & Print
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 bg-slate-50">
          <div className="space-y-4 max-w-[1400px] mx-auto">
          <Card className="p-5 border-slate-200">
            <h2 className="font-semibold text-slate-900 mb-3" style={{ fontFamily: "Work Sans" }}>Patient</h2>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Patient Name</Label>
                <Autocomplete
                  value={patientQuery}
                  onChange={(v) => { setPatientQuery(v); setPatient(null); }}
                  options={patients}
                  getLabel={(p) => p.full_name}
                  getKey={(p) => p.id}
                  onSelect={(p) => { setPatient(p); setPatientQuery(p.full_name); }}
                  placeholder="Search patient..."
                  testid="patient-autocomplete"
                />
              </div>
              <div><Label>Age</Label><Input value={patient?.age ?? ""} readOnly data-testid="patient-age" /></div>
              <div><Label>Gender</Label><Input value={patient?.gender || ""} readOnly data-testid="patient-gender" /></div>
              <div className="col-span-2"><Label>Address</Label><Input value={patient?.address || ""} readOnly /></div>
              <div><Label>Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="prescription-date" /></div>
              <div>
                <Label>Schedule next follow-up date</Label>
                <Input type="date" value={followUpDate} onChange={(e) => setFollowUpDate(e.target.value)} data-testid="prescription-followup-date" />
                <div className="text-[11px] text-slate-400 mt-1">Optional — prints on the Rx and adds the patient to Follow-ups.</div>
              </div>
              <div className="flex items-end">
                <Button type="button" variant="outline" onClick={useLastForPatient} className="w-full" data-testid="btn-use-last">
                  <Wand2 size={14} className="mr-2" /> Use Last Prescription
                </Button>
              </div>
            </div>
          </Card>

          <Card className="p-5 border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-slate-900" style={{ fontFamily: "Work Sans" }}>Diagnosis</h2>
              <span className="text-xs text-slate-500">Optional — prints on the Rx body. You may create an Rx with only a diagnosis (no medicines).</span>
            </div>
            <Textarea
              rows={2}
              value={diagnosis}
              onChange={(e) => setDiagnosis(e.target.value)}
              placeholder="e.g. Community-acquired pneumonia; Hypertension Stage 1"
              data-testid="rx-diagnosis-input"
            />
          </Card>

          <Card className="p-5 border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="font-semibold text-slate-900" style={{ fontFamily: "Work Sans" }}>Requests</h2>
                <p className="text-xs text-slate-500 mt-1">Each request group prints as its own separate page for the correct department.</p>
              </div>
              {!requestOpen ? (
                <Button size="sm" variant="outline" onClick={() => addRequestGroup("laboratory")} data-testid="btn-add-request">
                  <Plus size={14} className="mr-1" /> Add Request
                </Button>
              ) : (
                <button
                  type="button"
                  onClick={() => { setRequestOpen(false); setRequestText(""); setRequestGroups([]); }}
                  className="text-xs text-red-600 hover:underline"
                  data-testid="btn-remove-request"
                >
                  Remove All
                </button>
              )}
            </div>

            {requestOpen ? (
              <div className="space-y-3">
                {requestGroups.length === 0 ? (
                  <div className="border border-dashed border-slate-300 rounded-md p-4 text-center bg-slate-50">
                    <p className="text-sm text-slate-600 mb-3">No request groups yet.</p>
                    <Button type="button" size="sm" variant="outline" onClick={() => addRequestGroup("laboratory")}>
                      <Plus size={14} className="mr-1" /> Add Laboratory Request
                    </Button>
                  </div>
                ) : null}

                {requestGroups.map((group, idx) => (
                  <div key={idx} className="border border-slate-200 rounded-lg p-4 bg-white" data-testid={`request-group-${idx}`}>
                    <div className="grid md:grid-cols-3 gap-3 mb-3">
                      <div>
                        <Label>Request Type</Label>
                        <select
                          className="w-full h-10 border border-slate-200 rounded-md px-3 text-sm bg-white"
                          value={group.type || "other"}
                          onChange={(e) => updateRequestGroup(idx, { type: e.target.value, title: requestTypeLabel(e.target.value) })}
                          data-testid={`request-type-${idx}`}
                        >
                          {REQUEST_TYPE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <Label>Printed Heading</Label>
                        <Input
                          value={group.title || ""}
                          onChange={(e) => updateRequestGroup(idx, { title: e.target.value })}
                          placeholder="e.g. Laboratory Request"
                          data-testid={`request-title-${idx}`}
                        />
                      </div>
                    </div>

                    <Label>Request Items</Label>
                    <Textarea
                      rows={4}
                      value={group.items || ""}
                      onChange={(e) => updateRequestGroup(idx, { items: e.target.value })}
                      placeholder={"One item per line.\ne.g. CBC\nFBS\nLipid Profile"}
                      data-testid={`request-items-${idx}`}
                    />

                    <div className="mt-3">
                      <Label>Notes / Purpose</Label>
                      <Textarea
                        rows={2}
                        value={group.notes || ""}
                        onChange={(e) => updateRequestGroup(idx, { notes: e.target.value })}
                        placeholder="Optional notes, diagnosis, special instructions..."
                        data-testid={`request-notes-${idx}`}
                      />
                    </div>

                    <div className="flex justify-between items-center mt-3">
                      <p className="text-xs text-slate-500">This group prints as a separate request page.</p>
                      <Button type="button" size="sm" variant="outline" onClick={() => removeRequestGroup(idx)} data-testid={`request-remove-${idx}`}>
                        Remove Group
                      </Button>
                    </div>
                  </div>
                ))}

                <div className="flex flex-wrap gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={() => addRequestGroup("laboratory")}>+ Laboratory</Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => addRequestGroup("radiology")}>+ X-Ray / Imaging</Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => addRequestGroup("cardiology")}>+ ECG / Cardiology</Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => addRequestGroup("other")}>+ Other</Button>
                </div>
              </div>
            ) : (
              <p className="text-xs text-slate-500">Add laboratory, x-ray, ECG, ultrasound, procedure, referral, or other request slips. Each category prints separately.</p>
            )}
          </Card>

          <Card className="p-5 border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-slate-900" style={{ fontFamily: "Work Sans" }}>Medicines</h2>
              <Button size="sm" variant="outline" onClick={addRow} data-testid="btn-add-row">
                <Plus size={14} className="mr-1" /> Add Row
              </Button>
            </div>

            <div className="space-y-2">
              <div className="grid grid-cols-24 text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-2" style={{ display: "grid", gridTemplateColumns: "4fr 1.5fr 2fr 1.5fr 1.5fr 0.35fr" }}>
                <div>Medicine</div>
                <div>Dosage</div>
                <div>Frequency</div>
                <div>Duration</div>
                <div className="text-center">Total Qty</div>
                <div></div>
              </div>
              {rows.map((row, i) => (
                <div key={i} className="rounded-md border border-slate-100 hover:border-slate-200 p-2" data-testid={`med-row-${i}`}>
                  <div className="gap-2 items-start" style={{ display: "grid", gridTemplateColumns: "4fr 1.5fr 2fr 1.5fr 1.5fr 0.35fr" }}>
                    <div>
                      <Autocomplete
                        value={row.medicine_name}
                        onChange={(v) => updateRow(i, { medicine_name: v, medicine_id: "" })}
                        options={medicines}
                        getLabel={(m) => `${m.medicine_name}${m.strength ? ` ${m.strength}` : ""}`}
                        getKey={(m) => m.id}
                        onSelect={(m) => pickMedicine(i, m)}
                        placeholder="Type or pick..."
                        testid={`med-name-${i}`}
                      />
                    </div>
                    <Input
                      value={row.dosage}
                      onChange={(e) => updateRow(i, { dosage: e.target.value })}
                      placeholder="1 tab"
                      data-testid={`med-dosage-${i}`}
                    />
                    <VocabCombo
                      kind="frequency"
                      vocab={vocab.frequency}
                      onVocabAdded={(v) => pushVocab("frequency", v)}
                      value={row.frequency}
                      onChange={(v) => updateRow(i, { frequency: v })}
                      placeholder="Once daily"
                      testid={`med-frequency-${i}`}
                    />
                    <VocabCombo
                      kind="duration"
                      vocab={vocab.duration}
                      onVocabAdded={(v) => pushVocab("duration", v)}
                      value={row.duration}
                      onChange={(v) => updateRow(i, { duration: v })}
                      placeholder="7 days"
                      testid={`med-duration-${i}`}
                    />
                    <div className="flex flex-col items-center gap-1">
                      <div className="flex items-center gap-1 w-full">
                        <Input
                          type="number"
                          min="0"
                          step="0.5"
                          value={row.total_quantity ?? ""}
                          onChange={(e) => updateRow(i, { total_quantity: e.target.value === "" ? null : Number(e.target.value) })}
                          placeholder="auto"
                          className="text-center"
                          data-testid={`med-total-qty-${i}`}
                          disabled={row.is_supply}
                        />
                        {row._qty_override && !row.is_supply ? (
                          <button type="button" onClick={() => resetQty(i)} title="Reset to auto-compute" className="text-blue-600 hover:text-blue-800" data-testid={`med-reset-qty-${i}`}>
                            <Calculator size={12} />
                          </button>
                        ) : null}
                      </div>
                      <VocabCombo
                        kind="unit"
                        vocab={vocab.unit}
                        onVocabAdded={(v) => pushVocab("unit", v)}
                        value={row.unit}
                        onChange={(v) => updateRow(i, { unit: v })}
                        placeholder="unit"
                        testid={`med-unit-${i}`}
                      />
                    </div>
                    <div className="flex flex-col gap-1 items-center">
                      <button type="button" onClick={() => moveRow(i, -1)} className="text-slate-400 hover:text-slate-700" data-testid={`med-up-${i}`}><ArrowUp size={12} /></button>
                      <button type="button" onClick={() => moveRow(i, 1)} className="text-slate-400 hover:text-slate-700" data-testid={`med-down-${i}`}><ArrowDown size={12} /></button>
                      <button type="button" onClick={() => removeRow(i)} className="text-red-500 hover:text-red-700" data-testid={`med-remove-${i}`}><Trash2 size={12} /></button>
                    </div>
                  </div>
                  {/* Row 2: instruction + is_supply toggle */}
                  <div className="flex items-center gap-3 mt-2 pl-2">
                    <label className="flex items-center gap-1 text-xs text-slate-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!row.is_supply}
                        onChange={(e) => updateRow(i, { is_supply: e.target.checked })}
                        data-testid={`med-supply-${i}`}
                        className="rounded border-slate-300"
                      />
                      <Package size={12} /> Medical supply (no auto-compute)
                    </label>
                    {row.instruction !== undefined && row.instruction !== "" ? (
                      <Input
                        className="text-xs flex-1"
                        value={row.instruction}
                        onChange={(e) => updateRow(i, { instruction: e.target.value })}
                        placeholder="Instruction (after meals, with water...)"
                        data-testid={`med-instruction-${i}`}
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => updateRow(i, { instruction: " " })}
                        className="text-[11px] text-blue-700 hover:underline"
                      >
                        + add instruction
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {/* Add-row at the bottom so you can keep adding as you fill down. */}
              <Button
                variant="outline"
                onClick={addRow}
                data-testid="btn-add-row-bottom"
                className="w-full border-dashed border-slate-300 text-slate-600 hover:border-blue-400 hover:text-blue-700 mt-1"
              >
                <Plus size={14} className="mr-1" /> Add Row
              </Button>
            </div>

            <div className="mt-4">
              <Label>General Instructions / Notes</Label>
              <Textarea
                value={generalInstruction}
                onChange={(e) => setGeneralInstruction(e.target.value)}
                rows={3}
                placeholder="Drink plenty of water. Follow up after 2 weeks."
                data-testid="general-instruction"
              />
            </div>
          </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
